import type { EventSummary } from '@/contexts/AuthContext'

export type EventStatus = 'active' | 'upcoming' | 'completed'

export function deriveStatus(start?: string | null, end?: string | null): EventStatus {
  if (!start) return 'upcoming'
  const now = Date.now()
  const s = new Date(start).getTime()
  const e = end ? new Date(end).getTime() + 24 * 60 * 60 * 1000 : s + 24 * 60 * 60 * 1000
  if (now < s) return 'upcoming'
  if (now > e) return 'completed'
  return 'active'
}

export function shortenName(name: string): string {
  return name.replace(/\s+\d{4}\s*$/, '').trim() || name
}

export function formatDateRange(start?: string | null, end?: string | null): string {
  if (!start) return ''
  const s = new Date(start)
  if (!end) return s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const e = new Date(end)
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()
  if (sameMonth) {
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${e.getDate()}`
  }
  return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

export function extractYear(ev: EventSummary): number {
  if (ev.StartDate) return new Date(ev.StartDate).getFullYear()
  const m = ev.EventName.match(/\b(20\d{2})\b/)
  return m ? parseInt(m[1], 10) : new Date().getFullYear()
}

export function extractFamilyName(name: string): string {
  return name
    .replace(/\s+\d{4}\s*$/, '')
    .replace(/\s*#\d+\s*$/, '')
    .trim() || name
}

export interface EventFamily {
  name: string
  events: EventSummary[]
  years: number[]
}

export function groupIntoFamilies(events: EventSummary[]): EventFamily[] {
  const map = new Map<string, EventSummary[]>()
  for (const ev of events) {
    const family = extractFamilyName(ev.EventName)
    const existing = map.get(family) || []
    existing.push(ev)
    map.set(family, existing)
  }

  return Array.from(map.entries())
    .map(([name, evts]) => {
      const years = [...new Set(evts.map(extractYear))].sort((a, b) => b - a)
      const sorted = evts.sort((a, b) => {
        const sa = a.StartDate ? new Date(a.StartDate).getTime() : 0
        const sb = b.StartDate ? new Date(b.StartDate).getTime() : 0
        return sb - sa
      })
      return { name, events: sorted, years }
    })
    .sort((a, b) => {
      const aLive = a.events.some((e) => deriveStatus(e.StartDate, e.EndDate) === 'active')
      const bLive = b.events.some((e) => deriveStatus(e.StartDate, e.EndDate) === 'active')
      if (aLive && !bLive) return -1
      if (!aLive && bLive) return 1
      const aMax = Math.max(...a.events.map((e) => e.StartDate ? new Date(e.StartDate).getTime() : 0))
      const bMax = Math.max(...b.events.map((e) => e.StartDate ? new Date(e.StartDate).getTime() : 0))
      return bMax - aMax
    })
}
